/**
 * BrowserCameraAdapter - ブラウザのMediaStream APIを使用したカメラアダプター
 */

import type { 
  ICameraService, 
  CameraFrame, 
  CameraDevice, 
  CameraCapabilities,
  CameraConstraints 
} from './ICameraService'
import { logger } from '../../../lib/logger'

// MediaStreamTrackの拡張型定義
interface ExtendedMediaTrackCapabilities {
  zoom?: {
    min?: number
    max?: number
    step?: number
  }
  torch?: boolean
  facingMode?: string[] | string
  width?: { min?: number; max?: number }
  height?: { min?: number; max?: number }
  frameRate?: { min?: number; max?: number }
  aspectRatio?: { min?: number; max?: number }
}

type MediaStreamTrackWithCapabilities = MediaStreamTrack & {
  getCapabilities: () => ExtendedMediaTrackCapabilities
}

// MediaTrackSettings の拡張型定義
interface ExtendedMediaTrackSettings extends MediaTrackSettings {
  zoom?: number
  torch?: boolean
  facingMode?: 'user' | 'environment'
}

export class BrowserCameraAdapter implements ICameraService {
  private stream: MediaStream | null = null
  private videoElement: HTMLVideoElement | null = null
  private currentDeviceId: string | null = null
  private isActiveFlag = false

  async start(videoElement: HTMLVideoElement, constraints?: CameraConstraints): Promise<void> {
    logger.log('[BrowserCameraAdapter] start() called, isActive:', this.isActiveFlag)
    logger.log('[BrowserCameraAdapter] Stack trace:', new Error().stack)
    
    // 既に同じビデオ要素でアクティブな場合は何もしない
    if (this.isActiveFlag && this.videoElement === videoElement && !constraints) {
      logger.log('[BrowserCameraAdapter] Camera already active with same video element, skipping restart')
      return
    }
    
    try {
      // 新しい制約が指定された場合、または異なるビデオ要素の場合のみ停止
      if (this.stream && (constraints || this.videoElement !== videoElement)) {
        logger.log('[BrowserCameraAdapter] Stopping existing stream due to new constraints or different video element')
        this.stop()
      }

      // MediaStreamの制約を構築
      const mediaConstraints: MediaStreamConstraints = {
        video: {
          facingMode: constraints?.facingMode || 'environment',
          width: constraints?.width || { ideal: 1920 },
          height: constraints?.height || { ideal: 1080 },
          ...(constraints?.deviceId && { deviceId: constraints.deviceId })
        },
        audio: false
      }

      logger.log('[BrowserCameraAdapter] Requesting camera access with constraints:', mediaConstraints)
      
      // カメラアクセスを要求
      this.stream = await window.navigator.mediaDevices.getUserMedia(mediaConstraints)
      
      logger.log('[BrowserCameraAdapter] Got media stream:', this.stream)
      
      // ビデオ要素に接続
      this.videoElement = videoElement
      videoElement.srcObject = this.stream
      
      logger.log('[BrowserCameraAdapter] Video element configured')
      
      // 現在のデバイスIDを取得
      const videoTrack = this.stream.getVideoTracks()[0]
      if (videoTrack) {
        const settings = videoTrack.getSettings()
        this.currentDeviceId = settings.deviceId || null
        logger.log('[BrowserCameraAdapter] Current device:', this.currentDeviceId)
      }
      
      this.isActiveFlag = true
      logger.log('[BrowserCameraAdapter] Camera started successfully')
    } catch (error) {
      logger.error('[BrowserCameraAdapter] Failed to start camera:', error)
      throw new Error(`カメラの起動に失敗しました: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  stop(): void {
    logger.log('[BrowserCameraAdapter] stop() called')
    
    if (this.stream) {
      // すべてのトラックを停止
      const tracks = this.stream.getTracks()
      logger.log('[BrowserCameraAdapter] Stopping', tracks.length, 'tracks')
      tracks.forEach(track => track.stop())
      this.stream = null
    }
    
    if (this.videoElement) {
      this.videoElement.srcObject = null
      this.videoElement = null
    }
    
    this.currentDeviceId = null
    this.isActiveFlag = false
    logger.log('[BrowserCameraAdapter] Camera stopped')
  }

  async capture(): Promise<CameraFrame> {
    logger.log('[BrowserCameraAdapter] capture() called')
    
    if (!this.videoElement || !this.stream) {
      logger.error('[BrowserCameraAdapter] Camera not active - videoElement:', this.videoElement, 'stream:', this.stream)
      throw new Error('カメラが起動していません')
    }

    // キャンバスを作成して画像をキャプチャ
    const canvas = document.createElement('canvas')
    const video = this.videoElement
    
    logger.log('[BrowserCameraAdapter] Video dimensions:', {
      videoWidth: video.videoWidth,
      videoHeight: video.videoHeight,
      readyState: video.readyState
    })
    
    // ビデオが準備できているか確認
    if (video.readyState < 2) {
      logger.error('[BrowserCameraAdapter] Video not ready, readyState:', video.readyState)
      throw new Error('ビデオがまだ準備できていません')
    }
    
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    
    const ctx = canvas.getContext('2d')
    if (!ctx) {
      logger.error('[BrowserCameraAdapter] Failed to get canvas context')
      throw new Error('Canvas context creation failed')
    }
    
    // ビデオフレームを描画
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
    logger.log('[BrowserCameraAdapter] Frame drawn to canvas')
    
    // BlobとDataURLを生成
    return new Promise((resolve, reject) => {
      logger.log('[BrowserCameraAdapter] Creating blob from canvas...')
      
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            logger.error('[BrowserCameraAdapter] Failed to create blob from canvas')
            reject(new Error('Failed to create blob'))
            return
          }
          
          logger.log('[BrowserCameraAdapter] Blob created, size:', blob.size)
          
          const dataUrl = canvas.toDataURL('image/jpeg', 0.9)
          logger.log('[BrowserCameraAdapter] DataURL created, length:', dataUrl.length)
          
          const frame = {
            blob,
            dataUrl,
            width: canvas.width,
            height: canvas.height,
            timestamp: Date.now()
          }
          
          logger.log('[BrowserCameraAdapter] Capture successful:', {
            width: frame.width,
            height: frame.height,
            timestamp: frame.timestamp
          })
          
          resolve(frame)
        },
        'image/jpeg',
        0.9
      )
    })
  }

  async getAvailableCameras(): Promise<CameraDevice[]> {
    try {
      // デバイスリストを取得
      const devices = await window.navigator.mediaDevices.enumerateDevices()
      
      // ビデオ入力デバイスをフィルタリングして変換
      const cameras = devices
        .filter(device => device.kind === 'videoinput')
        .map(device => {
          // デバイス名から向きを推測
          const label = device.label.toLowerCase()
          let facingMode: 'user' | 'environment' | 'unknown' = 'unknown'
          
          if (label.includes('front') || label.includes('前')) {
            facingMode = 'user'
          } else if (label.includes('back') || label.includes('rear') || label.includes('後')) {
            facingMode = 'environment'
          }
          
          return {
            deviceId: device.deviceId,
            label: device.label || `Camera ${device.deviceId.slice(0, 8)}`,
            kind: (facingMode === 'user' ? 'front' : 'normal') as 'wide' | 'normal' | 'telephoto' | 'front',
            facingMode,
            capabilities: {
              zoom: { min: 1, max: 1, step: 0.1 }, // デフォルト値（後で実際の値を取得）
              torch: false
            }
          }
        })
      
      // 現在のストリームから詳細な機能を取得
      if (this.stream) {
        const videoTrack = this.stream.getVideoTracks()[0]
        if (videoTrack && 'getCapabilities' in videoTrack) {
          const trackWithCapabilities = videoTrack as MediaStreamTrackWithCapabilities
          const capabilities = trackWithCapabilities.getCapabilities()
          
          // 現在のカメラの機能を更新
          const currentCamera = cameras.find(cam => cam.deviceId === this.currentDeviceId)
          if (currentCamera && capabilities) {
            const extCapabilities = capabilities as ExtendedMediaTrackCapabilities
            if (extCapabilities.zoom) {
              currentCamera.capabilities.zoom = {
                min: extCapabilities.zoom.min || 1,
                max: extCapabilities.zoom.max || 1,
                step: extCapabilities.zoom.step || 0.1
              }
            }
            
            if (extCapabilities.torch) {
              currentCamera.capabilities.torch = true
            }
          }
        }
      }
      
      return cameras
    } catch (error) {
      logger.error('[BrowserCameraAdapter] Failed to get cameras:', error)
      return []
    }
  }

  async selectCamera(deviceId: string): Promise<void> {
    if (!this.videoElement) {
      throw new Error('Video element not initialized')
    }
    
    // 新しいデバイスIDで再起動
    await this.start(this.videoElement, { deviceId })
  }

  async switchCamera(): Promise<void> {
    const cameras = await this.getAvailableCameras()
    if (cameras.length < 2) {
      throw new Error('複数のカメラが見つかりません')
    }
    
    // 現在のカメラのインデックスを取得
    const currentIndex = cameras.findIndex(cam => cam.deviceId === this.currentDeviceId)
    const nextIndex = (currentIndex + 1) % cameras.length
    const nextCamera = cameras[nextIndex]
    
    if (!nextCamera) {
      throw new Error('次のカメラが見つかりません')
    }
    
    // 次のカメラに切り替え
    await this.selectCamera(nextCamera.deviceId)
  }

  async setZoom(factor: number): Promise<void> {
    if (!this.stream) {
      throw new Error('Stream not initialized')
    }
    
    const videoTrack = this.stream.getVideoTracks()[0]
    if (!videoTrack) {
      throw new Error('No video track found')
    }
    
    // ズーム機能がサポートされているか確認
    if ('applyConstraints' in videoTrack) {
      try {
        await videoTrack.applyConstraints({
          advanced: [{ zoom: factor } as Record<string, unknown>]
        })
      } catch (error) {
        logger.warn('[BrowserCameraAdapter] Zoom not supported:', error)
        // ズームがサポートされていない場合は無視
      }
    }
  }

  async setTorch(enabled: boolean): Promise<void> {
    if (!this.stream) {
      throw new Error('Stream not initialized')
    }
    
    const videoTrack = this.stream.getVideoTracks()[0]
    if (!videoTrack) {
      throw new Error('No video track found')
    }
    
    // トーチ機能がサポートされているか確認
    if ('applyConstraints' in videoTrack) {
      try {
        await videoTrack.applyConstraints({
          advanced: [{ torch: enabled } as Record<string, unknown>]
        })
      } catch (error) {
        logger.warn('[BrowserCameraAdapter] Torch not supported:', error)
        // トーチがサポートされていない場合は無視
      }
    }
  }

  getCapabilities(): CameraCapabilities | null {
    if (!this.stream) {
      return null
    }
    
    const videoTrack = this.stream.getVideoTracks()[0]
    if (!videoTrack) {
      return null
    }
    
    const settings = videoTrack.getSettings()
    const capabilities: CameraCapabilities = {
      resolution: {
        width: settings.width || 1920,
        height: settings.height || 1080
      }
    }
    
    // getCapabilitiesがサポートされている場合
    if ('getCapabilities' in videoTrack) {
      const trackWithCaps = videoTrack as MediaStreamTrackWithCapabilities
      const trackCaps = trackWithCaps.getCapabilities()
      
      const extTrackCaps = trackCaps as ExtendedMediaTrackCapabilities
      if (extTrackCaps.zoom) {
        capabilities.zoom = {
          min: extTrackCaps.zoom.min || 1,
          max: extTrackCaps.zoom.max || 1,
          current: (settings as ExtendedMediaTrackSettings).zoom || 1
        }
      }
      
      if (extTrackCaps.torch) {
        capabilities.torch = {
          supported: true,
          enabled: (settings as ExtendedMediaTrackSettings).torch || false
        }
      }
      
      if (trackCaps.facingMode) {
        const facingMode = Array.isArray(trackCaps.facingMode) ? trackCaps.facingMode[0] : trackCaps.facingMode
        if (facingMode === 'user' || facingMode === 'environment') {
          capabilities.facingMode = facingMode
        }
      }
    }
    
    // 現在のデバイス情報を追加
    if (this.currentDeviceId) {
      capabilities.currentDevice = {
        deviceId: this.currentDeviceId,
        label: settings.deviceId || 'Unknown Camera',
        kind: 'normal',
        facingMode: (settings as ExtendedMediaTrackSettings).facingMode || 'unknown',
        capabilities: {
          zoom: capabilities.zoom ? 
            { min: capabilities.zoom.min, max: capabilities.zoom.max, step: 0.1 } : 
            { min: 1, max: 1, step: 0.1 },
          torch: !!capabilities.torch?.supported
        }
      }
    }
    
    return capabilities
  }

  isActive(): boolean {
    return this.isActiveFlag && this.stream !== null
  }
}